import { Check, CircleDashed, OctagonAlert, TriangleAlert } from "lucide-react";
import type { Gate } from "../../lib/qa";

/**
 * GATE CHECKLIST — what QA checked, in plain language, with each check's real state.
 *
 * The old UI showed a bare "Passed" with no explanation of what QA even is. This names each of the
 * six checks, says what it verifies in one plain sentence, shows its real state (shape + word, never
 * colour alone), and — on a non-pass — the real issues. It never prints "passed" for a check that
 * did not run (the Gate type makes that uncompilable). Positive evidence numbers arrive in Phase 0.
 */
const EXPLAINER: Record<string, string> = {
  "protected-facts": "The numbers, dates, currencies, citations and defined terms that must survive verbatim.",
  "editable-blocks": "The edited sections stay within their length budget and never alter structure.",
  "research-rigor": "Every kept edit is backed by real, independent sources.",
  "patch-application": "The approved edits actually landed in the final document.",
  rendering: "The pages still render and fit their layout without overflow.",
  reviewers: "A five-lens advisory read — tone, faithfulness, audience fit, research use, and risk."
};

const STATE_WORD: Record<string, string> = {
  passed: "Passed",
  failed: "Must fix",
  blocked: "Must fix",
  warned: "Advisory",
  running: "Checking",
  not_run: "Not run",
  not_applicable: "N/A"
};

function GateGlyph({ state }: { state: string }) {
  if (state === "passed") return <Check aria-hidden="true" />;
  if (state === "failed" || state === "blocked") return <OctagonAlert aria-hidden="true" />;
  if (state === "warned") return <TriangleAlert aria-hidden="true" />;
  return <CircleDashed aria-hidden="true" />;
}

export function GateChecklist({ gates }: { gates: Gate[] }) {
  return (
    <section className="gate-checklist" data-testid="gate-checklist">
      <h2 className="gate-checklist-title">The six checks</h2>
      <ul className="gate-list">
        {gates.map((gate) => {
          // Protected facts is the one hard stop; everything else that isn't a pass is advisory.
          const hard = gate.id === "protected-facts" && (gate.state === "failed" || gate.state === "blocked");
          const word = hard ? "Must fix" : gate.state === "failed" || gate.state === "blocked" ? "Advisory" : STATE_WORD[gate.state] || "Not run";
          return (
            <li key={gate.id} className="gate-row" data-state={gate.state} data-hard={hard ? "true" : "false"}>
              <span className="gate-glyph" aria-hidden="true">
                <GateGlyph state={gate.state} />
              </span>
              <span className="gate-body">
                <span className="gate-head">
                  <span className="gate-label">{gate.label}</span>
                  <span className="gate-state" data-hard={hard ? "true" : "false"}>{word}</span>
                </span>
                <span className="gate-explainer">{EXPLAINER[gate.id] || ""}</span>
                {gate.state !== "passed" && gate.messages.length ? (
                  <ul className="gate-issues">
                    {gate.messages.slice(0, 4).map((message, index) => (
                      <li key={index}>{message}</li>
                    ))}
                  </ul>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
